<p-progressBar mode="indeterminate" *ngIf="loading"></p-progressBar>

<app-page-shell *ngIf="!loading && workPackage as work">
  <app-page-detail-hero
    eyebrow="Field Work Package"
    [title]="work.workPackageNumber || 'Field work package'"
    [subtitle]="work.title || ''"
    icon="fa-solid fa-people-carry-box">
    <div tags>
      <p-tag [value]="work.status || 'DRAFT'" [severity]="statusSeverity(work.status)"></p-tag>
      <p-tag [value]="work.activityType || 'GENERAL'" severity="info"></p-tag>
      <p-tag *ngIf="work.billable" value="BILLABLE" severity="contrast"></p-tag>
    </div>
    <div actions>
      <p-button label="Back to packages" icon="pi pi-arrow-left" severity="secondary" [outlined]="true"
        [routerLink]="['/operations/field-work/work-packages']"></p-button>
      <p-button *ngIf="targetRoute() as route" label="Open target" icon="pi pi-external-link"
        severity="secondary" [outlined]="true" [routerLink]="route"></p-button>
      <p-button label="Workforce Finance" icon="pi pi-wallet" severity="secondary" [outlined]="true"
        [routerLink]="['/accounts/enterprise/workforce-finance']"
        [queryParams]="{ tab: 'EARNINGS', fieldWorkPackageId: work.fieldWorkPackageId }"></p-button>
      <p-button *ngIf="canChangePackage" label="Replan / move" icon="pi pi-calendar-clock"
        severity="secondary" [outlined]="true" (onClick)="openPackageAction()"></p-button>
      <p-button *ngIf="canPlanResources" [label]="planningActionLabel" icon="pi pi-users"
        (onClick)="openPlanning()"></p-button>
      <p-button *ngIf="canOpenExecution" [label]="work.status === 'COMPLETED' ? 'View actuals' : 'Execute work'"
        icon="pi pi-play-circle" (onClick)="openExecution()"></p-button>
    </div>
  </app-page-detail-hero>

  <div class="error-banner" *ngIf="error"><i class="pi pi-exclamation-circle"></i>{{ error }}</div>

  <div class="stage-banner" *ngIf="work.requirementOnly">
    <i class="pi pi-info-circle"></i>
    <div><strong>Requirements gathering stage</strong><span>Capture roles, capabilities, headcount and reusable equipment demand here. Assignment is enabled only in truck planning, bulk planning, or shipment delivery.</span></div>
  </div>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <div class="planning-alerts" *ngIf="(work.blockers?.length || 0) + (work.warnings?.length || 0) > 0">
    <div class="alert danger" *ngFor="let blocker of work.blockers"><i class="pi pi-ban"></i>{{ blocker }}</div>
    <div class="alert warning" *ngFor="let warning of work.warnings"><i class="pi pi-exclamation-triangle"></i>{{ warning }}</div>
  </div>

  <app-page-data-panel class="field-work-workspace-panel" title="Field Work Workspace"
    subtitle="Control work context, workforce coverage, equipment allocation, and related finance in one workspace.">
    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)" styleClass="field-work-tabs">
      <p-tablist>
        <p-tab value="overview">
          <span class="field-work-tab"><i class="fa-solid fa-map-location-dot"></i><span>Overview</span></span>
        </p-tab>
        <p-tab value="labor">
          <span class="field-work-tab"><i class="fa-solid fa-people-group"></i><span>Labor</span><p-badge [value]="(work.laborAssignments?.length || 0).toString()" severity="info"></p-badge></span>
        </p-tab>
        <p-tab value="equipment">
          <span class="field-work-tab"><i class="fa-solid fa-screwdriver-wrench"></i><span>Equipment</span><p-badge [value]="(work.equipmentAllocations?.length || 0).toString()" [severity]="(work.equipmentAllocations?.length || 0) > 0 ? 'success' : 'secondary'"></p-badge></span>
        </p-tab>
        <p-tab value="finance">
          <span class="field-work-tab"><i class="fa-solid fa-money-bill-transfer"></i><span>Finance</span></span>
        </p-tab>
      </p-tablist>
      <p-tabpanels>
        <p-tabpanel value="overview"><ng-container *ngTemplateOutlet="overviewTab"></ng-container></p-tabpanel>
        <p-tabpanel value="labor"><ng-container *ngTemplateOutlet="laborTab"></ng-container></p-tabpanel>
        <p-tabpanel value="equipment"><ng-container *ngTemplateOutlet="equipmentTab"></ng-container></p-tabpanel>
        <p-tabpanel value="finance">
          <section class="workspace-section finance-workspace">
            <div class="workspace-section-heading"><div><h3>Workforce Finance</h3><p>Approved labor actuals create immutable earnings. Cash, payouts, and accounting are managed in Workforce Finance.</p></div></div>
            <div class="finance-summary-grid">
              <div><i class="pi pi-users"></i><span>Labor assignments</span><strong>{{ work.laborAssignments?.length || 0 }}</strong></div>
              <div><i class="pi pi-money-bill"></i><span>Billing treatment</span><strong>{{ work.billable ? 'Billable' : 'Internal' }}</strong></div>
              <div><i class="pi pi-briefcase"></i><span>Project</span><strong>{{ work.logisticsProjectId ? ('#' + work.logisticsProjectId) : 'Not linked' }}</strong></div>
            </div>
            <p-button label="Open Workforce Finance" icon="pi pi-wallet" [routerLink]="['/accounts/enterprise/workforce-finance']" [queryParams]="{ tab: 'EARNINGS', fieldWorkPackageId: work.fieldWorkPackageId }"></p-button>
          </section>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </app-page-data-panel>

  <ng-template #overviewTab>
  <app-page-data-panel title="Operational context" subtitle="Primary target, schedule, customer, project, and location inherited through the target adapter.">
    <dl class="detail-grid">
      <div><dt>Target</dt><dd>{{ work.targetReference || work.targetType || '—' }}</dd><small>{{ work.targetType }}</small></div>
      <div><dt>Activity</dt><dd>{{ work.activityType || 'GENERAL' }}</dd></div>
      <div><dt>Planned start</dt><dd>{{ work.plannedStart | date:'medium' }}</dd></div>
      <div><dt>Planned end</dt><dd>{{ work.plannedEnd | date:'medium' }}</dd></div>
      <div><dt>Location</dt><dd>{{ work.locationName || 'Not specified' }}</dd></div>
      <div><dt>Customer</dt><dd>{{ work.customerCrmClientId ? ('#' + work.customerCrmClientId) : 'Not resolved' }}</dd></div>
      <div><dt>Logistics project</dt><dd>{{ work.logisticsProjectId ? ('#' + work.logisticsProjectId) : 'Not linked' }}</dd></div>
      <div><dt>Billing</dt><dd>{{ work.billable ? 'Billable' : 'Internal / non-billable' }}</dd></div>
    </dl>
    <div class="notes" *ngIf="work.notes"><strong>Notes</strong><p>{{ work.notes }}</p></div>
  </app-page-data-panel>
  </ng-template>

  <ng-template #laborTab>
  <app-page-data-panel title="Labor requirements" subtitle="Roles, skills, headcount, and current assignment coverage."
    [resultText]="(work.laborRequirements?.length || 0) + ' roles'">
    <p-table [value]="work.laborRequirements || []" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Role</th><th>Required</th><th>Assigned</th><th>Skills</th><th>Coverage</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.roleName }}</strong><small>{{ row.roleCode }}</small></td><td>{{ row.headcount || 0 }}</td><td>{{ assignedTo(row) }}</td>
        <td><span class="skill-chip" *ngFor="let skill of row.skills">{{ skill.skillName }}<small>{{ skill.requirementType }}</small></span><span *ngIf="!row.skills?.length">—</span></td>
        <td><p-tag [value]="assignedTo(row) >= (row.headcount || 0) ? 'COVERED' : 'GAP'" [severity]="assignedTo(row) >= (row.headcount || 0) ? 'success' : 'danger'"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="5" class="empty-state">No labor roles configured.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel title="Labor assignments" subtitle="Assigned people retain ownership, payee, rate snapshot, and requirement traceability."
    [resultText]="(work.laborAssignments?.length || 0) + ' people'">
    <p-table [value]="work.laborAssignments || []" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Labor</th><th>Ownership</th><th>Planned window</th><th>Quantity</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.workerName || row.workerNumber || ('#' + row.fieldWorkerId) }}</strong><small>{{ row.workerNumber }}</small></td>
        <td><p-tag [value]="row.sourceType || 'INTERNAL'" severity="info"></p-tag></td><td>{{ row.plannedStart | date:'short' }}<small>{{ row.plannedEnd | date:'short' }}</small></td>
        <td>{{ row.plannedQuantity }} {{ row.billingUnit }}</td><td><p-tag [value]="row.assignmentStatus || 'PLANNED'" [severity]="statusSeverity(row.assignmentStatus)"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="5" class="empty-state">No labor assigned.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>
  </ng-template>

  <ng-template #equipmentTab>
  <app-page-data-panel title="Equipment requirements" subtitle="Mandatory and preferred resource demand with allocation coverage."
    [resultText]="(work.equipmentRequirements?.length || 0) + ' requirements'">
    <p-table [value]="work.equipmentRequirements || []" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Category</th><th>Type</th><th>Required</th><th>Allocated</th><th>Location</th><th>Coverage</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.categoryName || ('#' + row.equipmentCategoryId) }}</strong></td><td>{{ row.requirementType }}</td>
        <td>{{ row.requiredQuantity || 0 }}</td><td>{{ row.allocatedQuantity || 0 }}</td><td>{{ row.locationName || work.locationName || '—' }}</td>
        <td><p-tag [value]="(row.allocatedQuantity || 0) >= (row.requiredQuantity || 0) ? 'COVERED' : 'GAP'" [severity]="(row.allocatedQuantity || 0) >= (row.requiredQuantity || 0) ? 'success' : row.requirementType === 'MANDATORY' ? 'danger' : 'warn'"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No equipment requirements configured.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel title="Equipment allocations" subtitle="Reservations, custody, condition, and financial treatment for reusable assets."
    [resultText]="(work.equipmentAllocations?.length || 0) + ' allocations'">
    <p-table [value]="work.equipmentAllocations || []" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Resource</th><th>Ownership</th><th>Quantity</th><th>Custodian</th><th>Condition</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.equipmentResourceName || ('#' + row.equipmentResourceId) }}</strong><small>{{ row.financialTreatment }}</small></td>
        <td>{{ row.ownershipType }}</td><td>{{ row.plannedQuantity }} {{ row.billingUnit }}</td><td>{{ row.custodianLaborAssignmentId ? ('Assignment #' + row.custodianLaborAssignmentId) : 'Unassigned' }}</td>
        <td>{{ row.incomingCondition || row.outgoingCondition || '—' }}</td><td><p-tag [value]="row.allocationStatus || 'PLANNED'" [severity]="statusSeverity(row.allocationStatus)"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No equipment allocated.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>
  </ng-template>
</app-page-shell>

<app-field-work-labor-planning-dialog
  [visible]="planningVisible()"
  (visibleChange)="planningVisible.set($event)"
  [work]="workPackage"
  [canManage]="canManage"
  [canAssign]="canAssign"
  [canRelease]="canRelease"
  [canViewEquipment]="canViewEquipment"
  [canManageEquipment]="canManageEquipment"
  [canOverride]="canOverride"
  [canViewLaborRates]="canViewLaborRates"
  [canViewEquipmentRates]="canViewEquipmentRates"
  (changed)="onPackageChanged($event)">
</app-field-work-labor-planning-dialog>

<app-field-work-execution-dialog
  [visible]="executionVisible()"
  (visibleChange)="executionVisible.set($event)"
  [work]="workPackage"
  [canExecute]="canExecute"
  [canApprove]="canApprove"
  [canCheckout]="canCheckout"
  [canRelease]="canRelease"
  [canViewEquipment]="canViewEquipment"
  [canViewRates]="canViewRates"
  (changed)="onPackageChanged($event)">
</app-field-work-execution-dialog>

<p-dialog
  header="Change Field Work Package"
  [visible]="packageActionVisible()"
  (visibleChange)="packageActionVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!packageActionSaving()"
  appendTo="body"
  [style]="{ width: 'min(900px, 96vw)' }"
  styleClass="master-create-dialog field-package-action-dialog">
  <section class="package-action-dialog">
    <aside>
      <div class="dialog-icon"><i class="pi pi-calendar-clock"></i></div>
      <small>FIELD WORK CONTROL</small>
      <h3>{{ workPackage?.workPackageNumber }}</h3>
      <p>Replan the same target, move the package, or create an audited split. Existing approved history is never deleted.</p>
      <dl>
        <div><dt>Current target</dt><dd>{{ workPackage?.targetReference }}</dd></div>
        <div><dt>Status</dt><dd>{{ workPackage?.status }}</dd></div>
      </dl>
    </aside>
    <div class="package-action-form">
      <label>Action
        <p-select [(ngModel)]="packageActionMode"
          [options]="canOverride ? [{label:'Replan schedule',value:'REPLAN'},{label:'Transfer to another target',value:'TRANSFER'},{label:'Split to another target',value:'SPLIT'}] : [{label:'Replan schedule',value:'REPLAN'}]"
          optionLabel="label" optionValue="value" appendTo="body"></p-select>
      </label>
      <label>Title
        <input pInputText [(ngModel)]="packageActionTitle">
      </label>
      <label>Planned start
        <p-datepicker [(ngModel)]="packageActionStart" [showTime]="true" hourFormat="12" [showIcon]="true" appendTo="body"></p-datepicker>
      </label>
      <label>Planned end
        <p-datepicker [(ngModel)]="packageActionEnd" [showTime]="true" hourFormat="12" [showIcon]="true" appendTo="body"></p-datepicker>
      </label>
      <ng-container *ngIf="packageActionMode !== 'REPLAN'">
        <label>Destination target type
          <p-select [(ngModel)]="packageActionTargetType" [options]="targetTypeOptions" [filter]="true" appendTo="body"></p-select>
        </label>
        <label>Destination target ID
          <p-inputNumber [(ngModel)]="packageActionTargetId" [useGrouping]="false" [min]="1"></p-inputNumber>
        </label>
      </ng-container>
      <label class="reason-field">Audited reason *
        <input pInputText [(ngModel)]="packageActionReason" placeholder="Why is this package being replanned, transferred, or split?">
      </label>
      <div class="dialog-note" *ngIf="packageActionMode !== 'REPLAN'">
        <i class="pi pi-info-circle"></i>The destination is authoritatively validated for tenant ownership, lifecycle, and supported activity.
      </div>
    </div>
  </section>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [outlined]="true" [disabled]="packageActionSaving()"
      (onClick)="packageActionVisible.set(false)"></p-button>
    <p-button [label]="packageActionMode === 'REPLAN' ? 'Save replan' : packageActionMode === 'TRANSFER' ? 'Transfer package' : 'Create split'"
      icon="pi pi-check" [loading]="packageActionSaving()" (onClick)="savePackageAction()"></p-button>
  </ng-template>
</p-dialog>

<div class="fatal-error" *ngIf="!loading && !workPackage && error">{{ error }}</div>
